<template>
  <page-template :is-loading="isLoading" :data-exists="ssoData !== null">
    <template #pageHeadline>
      {{ $t('ssoConfiguration.pageHeadline') }}
    </template>
    <template #secondLevelHeaderRight>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        @button-click="saveConfiguration()"
        :disabled="isSaveDisabled"
        :tooltip="saveTooltip"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns sso-configuration-columns">
        <div class="rbz-column is-6">
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="ssoData.enabled"
              :label="$t('ssoConfiguration.enabled')"
              class="active-toggle"
              data-qa="active-toggle"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="sso-login-name"
              v-model:inputValue="ssoData.title"
              class="title-input"
              data-qa="title-input"
              :label="$t('ssoConfiguration.loginName')"
              :placeholder="$t('ssoConfiguration.loginName')"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="providers"
              v-model:value="ssoData.provider"
              @update:value="providerChanged()"
              :label="$t('ssoConfiguration.provider')"
              class="provider-selection"
              data-qa="switch-provider-dropdown"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="protocols"
              v-model:value="ssoData.protocol"
              :label="$t('ssoConfiguration.protocol')"
              class="protocol-selection"
              data-qa="switch-protocol-dropdown"
            />
          </div>
          <div class="rbz-field" v-if="['azure', 'okta'].includes(ssoData.provider)">
            <rbz-form-input
              name="jwt"
              class="roles-claim-key-input"
              data-qa="roles-claim-key-input"
              :label="$t('ssoConfiguration.tokenGroupPropertyName')"
              :placeholder="$t('ssoConfiguration.tokenGroupPropertyName')"
              v-model:inputValue="ssoData.params.roles_claim_key"
            />
          </div>
          <div class="rbz-field" v-if="['google'].includes(ssoData.provider)">
            <rbz-dropdown
              :items="['email', 'id'].map((option) => [option, option])"
              v-model:value="ssoData.params.roles_claim_key"
              :label="$t('ssoConfiguration.tokenGroupPropertyName')"
              data-qa="roles-claim-key-dropdown"
            />
          </div>
          <div class="rbz-field">
            <label
              class="rbz-label rbz-label-list"
              :class="{ disabled: planetStore.isUpdateInProcess }"
            >
              {{ $t('ssoConfiguration.idp.title') }}
            </label>
            <div v-for="(role, index) in roles" :key="role.id" class="rbz-card white role-card">
              <div class="rbz-columns role-card-columns">
                <div class="rbz-column is-6">
                  <rbz-form-input
                    name="idp-group-input"
                    :label="$t('ssoConfiguration.idp.groupClaim')"
                    data-qa="idp-group-input"
                    :placeholder="$t('ssoConfiguration.idp.groupClaim')"
                    :ref="(el) => (groupClaimRefs[index] = el)"
                    :is-invalid="!isGroupClaimValid(groupClaimRefs[index], role.value.idp_group)"
                    :error-message-text="$t('ssoConfiguration.idp.groupClaimInvalid')"
                    v-model:inputValue="role.value.idp_group"
                  />
                </div>
                <div class="rbz-column button-wrapper-column">
                  <rbz-form-button
                    class="remove-role-option-button"
                    :disabled="ssoData.params.roles.length === 1"
                    data-qa="remove-role-button"
                    v-b-tooltip="$t('ssoConfiguration.idp.removeGroupClaimTooltip')"
                    variant="tertiary"
                    icon="IconDelete"
                    @button-click="removeRole(index)"
                  />
                </div>
              </div>
              <div class="rbz-field">
                <rbz-dropdown
                  v-if="role.value.waap_groups[0]"
                  :skip-sort="true"
                  :items="groupNames.map((name) => [name.value, name.title])"
                  v-model:value="role.value.waap_groups[0]"
                  :label="$t('ssoConfiguration.idp.waapRole')"
                  class="groups-selection"
                  data-qa="groups-dropdown"
                />
              </div>
            </div>
            <rbz-form-button
              :label="$t('ssoConfiguration.idp.add')"
              class="add-role-button"
              data-qa="add-another-role-button"
              variant="secondary"
              @button-click="addRole()"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="token-expiration-input"
              type="number"
              number-type="whole"
              data-qa="token-expiration-input"
              :label="$t('ssoConfiguration.tokenExpirationThreshold')"
              :placeholder="$t('ssoConfiguration.tokenExpirationThreshold')"
              suffix="seconds"
              v-model:inputValue="ssoData.params.token_expiration_threshold"
            />
          </div>
        </div>
        <div class="rbz-column is-6">
          <div v-if="ssoData.protocol === 'oauth2'">
            <label class="rbz-text-h3">
              {{ $t('ssoConfiguration.oauth2.title') }}
            </label>
            <div class="rbz-field">
              <rbz-form-input
                name="id-input"
                class="id-input"
                data-qa="id-input"
                :label="$t('ssoConfiguration.oauth2.clientId')"
                :placeholder="$t('ssoConfiguration.oauth2.clientId')"
                v-model:inputValue="ssoData.params.client_id"
                :is-invalid="!validateClientId"
              />
            </div>
            <div class="rbz-field">
              <rbz-form-input
                name="secret-input"
                class="secret-input"
                data-qa="secret-input"
                :label="$t('ssoConfiguration.oauth2.clientSecret')"
                :placeholder="$t('ssoConfiguration.oauth2.clientSecret')"
                v-model:inputValue="ssoData.params.client_secret"
                :is-invalid="!validateClientSecret"
              />
            </div>
            <div class="rbz-field" v-show="!['google'].includes(ssoData.provider)">
              <rbz-form-input
                name="issuer"
                class="issuer-input"
                data-qa="issuer-input"
                :label="$t('ssoConfiguration.oauth2.issuer')"
                :placeholder="$t('ssoConfiguration.oauth2.issuer')"
                v-model:inputValue="ssoData.params.issuer"
                :is-invalid="!validateIssuer"
              />
            </div>
          </div>
          <div v-if="ssoData.protocol === 'saml2'">
            <label class="rbz-text-h3">
              {{ $t('ssoConfiguration.saml2.title') }}
            </label>
            <div class="rbz-field">
              <rbz-form-input
                name="tenantId"
                class="tenant-id-input"
                data-qa="tenant-id-input"
                :label="$t('ssoConfiguration.saml2.tenantId')"
                :placeholder="$t('ssoConfiguration.saml2.tenantId')"
                v-model:inputValue="ssoData.params.tenantId"
                :is-invalid="!validateTenantId"
              />
            </div>
            <div class="rbz-field">
              <rbz-form-input
                name="entityId"
                class="entity-id-input"
                data-qa="entity-id-input"
                :label="$t('ssoConfiguration.saml2.entityId')"
                :placeholder="$t('ssoConfiguration.saml2.entityId')"
                v-model:inputValue="ssoData.params.entityId"
                :is-invalid="!validateEntityId"
              />
            </div>
            <div class="rbz-field">
              <label class="rbz-label">
                {{ $t('ssoConfiguration.saml2.identityCertificate') }}
              </label>
              <div class="file has-name">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    @input="loadFile($event)"
                    data-qa="file-input"
                    name="certFileInput"
                    accept=".cer, .CER"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <icon-upload />
                    </span>
                    <span class="file-label">
                      {{ $t('ssoConfiguration.saml2.chooseCertificateFile') }}
                    </span>
                  </span>
                  <span class="file-name">
                    {{ certFile?.name || $t('common.noFileChosen') }}
                  </span>
                </label>
              </div>
            </div>
            <div v-if="ssoData.params.cert_thumbprint" class="rbz-field">
              <rbz-form-input
                name="certThumbprint"
                data-qa="cert-thumbprint-input"
                :label="$t('ssoConfiguration.saml2.certificateThumbprint')"
                :placeholder="$t('ssoConfiguration.saml2.certificateThumbprint')"
                :inputValue="ssoData.params.cert_thumbprint"
                disabled
              />
            </div>
            <div class="rbz-field">
              <rbz-form-input
                name="loginURL"
                class="login-url-input"
                data-qa="login-url-input"
                :label="$t('ssoConfiguration.saml2.loginUrl')"
                :placeholder="$t('ssoConfiguration.saml2.loginUrl')"
                v-model:inputValue="ssoData.params.login_url"
                :is-invalid="!validateLoginUrl"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </page-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Protocol, RoleActionPair, Sso } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import _ from 'lodash';
import { AxiosResponse } from 'axios';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import { mapStores } from 'pinia';
import { usePlanetStore } from '@/store/PlanetStore';
import { useUserStore } from '@/store/UserStore';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import IconUpload from 'ui-component-library/assets/icons/IconUpload.vue';

type Role = {
  id: string;
  value: RoleActionPair;
};

export default defineComponent({
  name: 'SsoConfiguration',
  data() {
    const groupNames = [
      {
        title: 'WAAP viewer',
        value: 'waap-config-viewer',
      },
      {
        title: 'WAAP editor',
        value: 'waap-config-editor',
      },
      {
        title: 'WAAP manager',
        value: 'waap-manager',
      },
    ];
    const basicRole: RoleActionPair = {
      idp_group: '',
      waap_groups: [groupNames[0].value],
    };
    const basicSso: Sso = {
      enabled: false,
      title: '',
      provider: 'okta',
      protocol: 'oauth2',
      params: {
        token_expiration_threshold: 600,
        roles: [basicRole],
        roles_claim_key: '',
        client_id: '',
        client_secret: '',
        issuer: '',
        login_url: '',
        tenantId: '',
        entityId: '',
        x509cert: '',
      },
    };
    return {
      isLoading: true,
      groupNames: groupNames,
      basicRole: basicRole,
      basicSso: basicSso,
      ssoData: null as Sso,
      certFile: null as File,
      validateParamRegex: '.+',
      validateUrlParamRegex: '^(http|https)://[^ "]+$',
      providers: [
        ['okta', 'Okta'],
        ['azure', 'Microsoft Entra ID'],
        ['google', 'Google'],
      ],
      roles: null as Role[],
      groupClaimRefs: [],
    };
  },

  components: {
    IconUpload,
    RbzFormButton,
    RbzFormInput,
    RbzFormToggle,
    PageTemplate,
    RbzDropdown,
  },

  watch: {
    roles: {
      handler: function () {
        if (this.ssoData.params) {
          this.ssoData.params.roles = _.map(this.roles, 'value');
        }
      },
      deep: true,
    },
  },

  computed: {
    ...mapStores(usePlanetStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    validateLoginUrl(): boolean {
      const validLoginUrlPattern = new RegExp(this.validateUrlParamRegex);
      return validLoginUrlPattern.test(this.ssoData.params.login_url);
    },

    validateClientId(): boolean {
      const validClientIdPattern = new RegExp(this.validateParamRegex);
      return validClientIdPattern.test(this.ssoData.params.client_id);
    },

    validateClientSecret(): boolean {
      const validClientSecretPattern = new RegExp(this.validateParamRegex);
      return validClientSecretPattern.test(this.ssoData.params.client_secret);
    },

    validateIssuer(): boolean {
      const validIssuerPattern = new RegExp(this.validateParamRegex);
      return validIssuerPattern.test(this.ssoData.params.issuer);
    },

    validateTenantId(): boolean {
      const validTenantIdPattern = new RegExp(this.validateParamRegex);
      return validTenantIdPattern.test(this.ssoData.params.tenantId);
    },

    validateEntityId(): boolean {
      const validEntityIdPattern = new RegExp(this.validateParamRegex);
      return validEntityIdPattern.test(this.ssoData.params.entityId);
    },

    validateGroupClaim() {
      const allGroupClaims = _.map(_.map(this.roles, 'value'), 'idp_group');
      const duplicateGroupClaims = _.filter(allGroupClaims, (val, i, iteratee) =>
        _.includes(iteratee, val, i + 1)
      );
      return duplicateGroupClaims.length > 0;
    },

    saveTooltip(): string {
      if (this.isLoading) {
        return '';
      }
      if (this.validateGroupClaim) {
        return this.$t('ssoConfiguration.idp.groupClaimInvalid');
      }
      if (!this.ssoData.enabled || !this.ssoData.protocol) {
        return '';
      }
      return '';
    },

    isSaveDisabled(): boolean {
      if (this.isLoading) {
        return true;
      }
      if (this.validateGroupClaim) {
        return true;
      }
      if (!this.ssoData.enabled || !this.ssoData.protocol) {
        return false;
      }
      if (this.ssoData.protocol === 'oauth2') {
        return (
          !this.validateClientId ||
          !this.validateClientSecret ||
          (!['google'].includes(this.ssoData.provider) && !this.validateIssuer)
        );
      } else {
        // protocol === saml2
        return !this.validateLoginUrl || !this.validateTenantId || !this.validateEntityId;
      }
    },

    protocols(): [Protocol, string][] {
      const protocols: Protocol[] = [];
      if (this.ssoData.provider.includes('google')) {
        protocols.push('oauth2');
      }
      if (this.ssoData.provider.includes('azure')) {
        protocols.push('saml2');
      }
      if (this.ssoData.provider.includes('okta')) {
        protocols.push('oauth2', 'saml2');
      }
      return protocols.map((option) => {
        if (option === 'oauth2') {
          return [option, 'OAuth 2.0'];
        } else if (option === 'saml2') {
          return [option, 'SAML 2.0'];
        }
      });
    },
  },
  methods: {
    loadFile(event: Event) {
      const target = event.target as HTMLInputElement;
      const file: File = target.files[0];
      this.certFile = file;
      const extension = file?.name.split('.').pop();
      if (extension?.toLowerCase() === 'cer') {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = (event.target as FileReader).result;
          if (typeof content === 'string') {
            this.ssoData.params.x509cert = content;
          }
        };
        reader.readAsText(file);
      } else {
        target.value = null;
        this.certFile = null;
      }
    },

    addRole() {
      this.roles.push({
        id: DatasetsUtils.generateUUID2(),
        value: _.cloneDeep(this.basicRole),
      });
    },

    removeRole(index: number) {
      this.roles.splice(index, 1);
    },

    isGroupClaimValid(ref: RbzFormInput, groupClaim: string) {
      const isDuplicate =
        this.roles.filter((role: Role) => {
          return role.value.idp_group === groupClaim;
        }).length > 1;
      if (isDuplicate && ref) {
        ref.setIsDirty(true);
      }
      return !isDuplicate;
    },

    normalizeSso() {
      const basicSso = _.cloneDeep(this.basicSso);
      this.ssoData = {
        ...basicSso,
        ...this.ssoData,
      };
      this.ssoData.params = {
        ...basicSso.params,
        ...this.ssoData.params,
      };
    },

    reloadRolesArray() {
      this.roles = _.map(this.ssoData.params.roles, (keyValue) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          value: keyValue,
        };
      });
    },

    async loadConfiguration() {
      this.isLoading = true;
      const methodName = 'GET';
      const url = 'accounts/sso';
      const failureMessage = this.$t('ssoConfiguration.loadError');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName,
        url,
        failureMessage,
      });
      this.ssoData = response?.data || {};
      this.normalizeSso();
      this.reloadRolesArray();
      this.isLoading = false;
    },

    async saveConfiguration() {
      this.isLoading = true;
      const methodName = 'POST';
      let url = '';
      let correctParams: Sso['params'] = {
        roles_claim_key: this.ssoData.params.roles_claim_key,
        roles: this.ssoData.params.roles,
        token_expiration_threshold: this.ssoData.params.token_expiration_threshold,
      };
      if (this.ssoData.protocol === 'oauth2') {
        correctParams = {
          ...correctParams,
          client_id: this.ssoData.params.client_id,
          client_secret: this.ssoData.params.client_secret,
          issuer: this.ssoData.params.issuer,
        };
        url = 'accounts/sso/oauth2';
      } else if (this.ssoData.protocol === 'saml2') {
        correctParams = {
          ...correctParams,
          tenantId: this.ssoData.params.tenantId,
          entityId: this.ssoData.params.entityId,
          login_url: this.ssoData.params.login_url,
          x509cert: this.ssoData.params.x509cert,
          cert_thumbprint: this.ssoData.params.cert_thumbprint,
        };
        url = 'accounts/sso/saml2';
      }
      this.ssoData.params = correctParams;
      const data = this.ssoData;
      const successMessage = this.$t('common.operations.save.messages.success', {
        singular: this.$t('ssoConfiguration.entity'),
      });
      const failureMessage = this.$t('common.operations.save.messages.failure', {
        singular: this.$t('ssoConfiguration.entity'),
      });
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName,
        url,
        data,
        successMessage,
        failureMessage,
      }).then((response: AxiosResponse<Sso>) => {
        if (response?.status === 200) {
          this.loadConfiguration();
        }
      });
      this.isLoading = false;
    },

    providerChanged() {
      if (!this.protocols.some((protocol) => protocol[0] === this.ssoData.protocol)) {
        this.ssoData.protocol = this.protocols[0][0];
      }
      let rolesClaimKey;
      if (['google'].includes(this.ssoData.provider)) {
        rolesClaimKey = 'email';
      } else {
        rolesClaimKey = '';
      }
      const basicSso = _.cloneDeep(this.basicSso);
      this.ssoData = {
        ...basicSso,
        ...{
          enabled: this.ssoData.enabled,
          protocol: this.ssoData.protocol,
          provider: this.ssoData.provider,
        },
      };
      this.ssoData.params.roles_claim_key = rolesClaimKey;
      this.reloadRolesArray();
    },
  },
  mounted() {
    this.loadConfiguration();
  },
});
</script>

<style scoped lang="scss">
.sso-configuration-columns {
  margin-top: 2px;
}

.role-card {
  margin-bottom: 8px;
  padding: 20px;

  .role-card-columns {
    margin-bottom: 16px;
  }
}

:deep(.button-wrapper-column) {
  display: flex;
  justify-content: flex-end;

  .rbz-button-wrapper {
    margin-left: auto;
  }
}

.file-cta,
.file-name {
  align-items: center;
  display: flex;
  height: 40px;
}
</style>
